import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Package } from 'lucide-react';
import { useFetch } from '@/shared/hooks/useFetch';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiParcelsListGet } from '@/entities/parcels';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { FulfillmentChip } from '@/shared/ui/chips/FulfillmentChip';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { extractErrorMessage } from '@/shared/api/errors';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { formatDate } from '@/shared/utils/date';
import type { ApiParcelsGetItem } from '@/shared/types/api/parcels';

const PAGE_SIZE = 20;

/**
 * Tab 1 of the Omborxona page. Lists parcels that already have an owner and
 * are physically inside the JP warehouse. 2026-05-31 owner spec: scope is
 * ``is_in_warehouse=true`` (canonical flag) AND the workflow_status is in
 * one of the three "in-warehouse" states (IN_PROGRESS, SORTING, COLLECTING).
 * The workflow filter is kept as a belt-and-braces guard so a transient
 * inconsistency between the flag and the chip can't leak unrelated rows.
 */
export const WarehouseOwnedTab = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);

  const params = useMemo(
    () =>
      ({
        page,
        page_size: PAGE_SIZE,
        search: debouncedSearch.trim() || undefined,
        has_owner: 'true',
        is_in_warehouse: 'true',
        workflow_status__in: 'IN_PROGRESS,SORTING,COLLECTING',
      }) as const,
    [page, debouncedSearch],
  );

  const query = useFetch({
    api: () => apiParcelsListGet(params),
    keywords: queryKeys.warehouse.owned(params),
    staleTime: 60_000,
  });

  const columns = useMemo<ColumnDef<ApiParcelsGetItem>[]>(
    () => [
      {
        accessorKey: 'trek_id',
        header: t('warehouse.owned.columns.trekId'),
        cell: (info) => (
          <span className="font-mono text-xs">{info.row.original.trek_id}</span>
        ),
      },
      {
        accessorKey: 'name',
        header: t('warehouse.owned.columns.name'),
        cell: (info) => (
          <span className="truncate font-medium text-ink">{info.row.original.name || '—'}</span>
        ),
      },
      {
        id: 'owner',
        header: t('warehouse.owned.columns.owner'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const owner = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
          if (!owner) return <span className="text-ink-muted">—</span>;
          return <span className="truncate">{owner}</span>;
        },
      },
      {
        id: 'fulfillment',
        header: t('warehouse.owned.columns.fulfillment'),
        enableSorting: false,
        cell: (info) =>
          info.row.original.fulfillment_status ? (
            <FulfillmentChip status={info.row.original.fulfillment_status} />
          ) : (
            <span className="text-ink-muted">—</span>
          ),
      },
      {
        accessorKey: 'created_at',
        header: t('warehouse.owned.columns.date'),
        cell: (info) => formatDate(info.row.original.created_at),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <FilterBar
        searchValue={search}
        onSearchChange={(v) => {
          setPage(1);
          setSearch(v);
        }}
        searchPlaceholder={t('warehouse.owned.searchPlaceholder')}
      />

      {query.error ? (
        <ErrorState
          message={extractErrorMessage(query.error, t('warehouse.owned.loadError'))}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <>
          <DataTable
            data={query.data?.results ?? []}
            columns={columns}
            isLoading={query.isLoading}
            emptyTitle={t('warehouse.owned.emptyTitle')}
            emptyDescription={t('warehouse.owned.emptyDescription')}
            emptyIcon={<Package className="h-8 w-8" aria-hidden />}
            onRowClick={(row) => navigate(`/parcels/${row.id}`)}
            getRowId={(row) => row.id}
            numbered
            pageIndex={page - 1}
            pageSize={PAGE_SIZE}
          />
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={query.data?.count ?? 0}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
};
